import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Doc, Id } from "@cvx/_generated/dataModel";
import { Eye, Info, Users, Video } from "lucide-react";
import { TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { TooltipProvider, Tooltip as UITooltip } from "@/components/ui/tooltip";
import { formatCurrency, formatNumber, truncateText } from "@/utils/format";
import { useEffect, useMemo } from "react";

import { Card } from "@/components/ui/card";
import PlatformIcon from "@/components/Generic/PlatformIcon";
import { api } from "@cvx/_generated/api";
import { convexQuery } from "@convex-dev/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

const staticDashboardSearchSchema = z.object({
  campaignIds: z.string().optional(),
});

export const Route = createFileRoute("/static-dashboard")({
  component: StaticDashboard,
  validateSearch: staticDashboardSearchSchema,
});

type Video = Doc<"contractVideos"> & {
  views?: number;
  likes?: number;
  comments?: number;
  shares?: number;
  bookmarks?: number;
  earnings?: number;
};

// Types for chart components
interface TooltipPayload {
  color: string;
  dataKey: string;
  value: number;
}

interface PieTooltipPayload {
  payload: {
    platform: string;
    views: number;
    percentage: number;
    color: string;
  };
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}

// Types for analytics data structure
interface HistoricalDataEntry {
  date: string;
  views: number;
  likes: number;
  comments: number;
  shares: number;
  videosWithData: number;
}

// Platform colors matching PlatformIcon.tsx
const PLATFORM_COLORS = {
  youtube: "#ef4444", // red-500
  instagram: "#ec4899", // pink-500
  tiktok: "hsl(var(--foreground))", // Theme-aware: dark in light mode, light in dark mode
  unknown: "#6B7280",
};

// Get TikTok color based on theme
const getTikTokColor = () => {
  if (typeof window !== "undefined") {
    const isDark = document.documentElement.classList.contains("dark");
    return isDark ? "#f5f5dc" : "#374151"; // beige for dark mode, dark grey for light mode
  }
  return "#374151"; // default to dark grey
};

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    // Calculate total for this date
    const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);

    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 dark:bg-gray-800 dark:border-gray-700">
        <p className="font-medium text-gray-900 dark:text-gray-100 mb-2">
          {new Date(label || "").toLocaleDateString()}
        </p>

        {/* Individual platform values */}
        {payload.map((entry: TooltipPayload, index: number) => (
          <div key={index} className="flex items-center gap-2 text-sm mb-1">
            <div
              className="w-3 h-3 rounded-full"
              style={{
                backgroundColor:
                  entry.dataKey === "tiktok" ? getTikTokColor() : entry.color,
              }}
            />
            <PlatformIcon platform={entry.dataKey} />
            <span className="capitalize text-gray-600 dark:text-gray-300">
              {entry.dataKey}:
            </span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {formatNumber(entry.value)}
            </span>
          </div>
        ))}

        {/* Total */}
        <div className="border-t border-gray-200 dark:border-gray-600 mt-2 pt-2">
          <div className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full bg-gray-500" />
            <span className="font-semibold text-gray-600 dark:text-gray-300">
              Total:
            </span>
            <span className="font-bold text-gray-900 dark:text-gray-100">
              {formatNumber(total)}
            </span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

// Custom pie chart tooltip
const PieTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: PieTooltipPayload[];
}) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 dark:bg-gray-800 dark:border-gray-700">
        <div className="flex items-center gap-2 text-sm">
          <div
            className="w-3 h-3 rounded-full"
            style={{
              backgroundColor:
                data.platform === "tiktok" ? getTikTokColor() : data.color,
            }}
          />
          <PlatformIcon platform={data.platform} />
          <span className="capitalize text-gray-600 dark:text-gray-300">
            {data.platform}:
          </span>
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {formatNumber(data.views)} views ({data.percentage}%)
          </span>
        </div>
      </div>
    );
  }
  return null;
};

function StaticDashboard() {
  const { campaignIds } = Route.useSearch();

  // Parse campaign IDs from comma-separated string
  const campaignIdsArray = campaignIds
    ? (campaignIds
        .split(",")
        .filter((id) => id.trim() !== "") as Id<"campaigns">[])
    : [];

  const { data, isLoading } = useQuery({
    ...convexQuery(api.dashboard.getApprovedVideosForCampaigns, {
      campaignIds: campaignIdsArray,
    }),
    enabled: campaignIdsArray.length > 0,
  });

  // Analytics queries
  const { data: analyticsData, isLoading: isAnalyticsLoading } = useQuery({
    ...convexQuery(api.analytics.getMultipleCampaignsVideoAnalytics, {
      campaignIds: campaignIdsArray,
    }),
    enabled: campaignIdsArray.length > 0,
  });

  const { data: leaderboardData, isLoading: isLeaderboardLoading } = useQuery({
    ...convexQuery(api.analytics.getMultipleCampaignsLeaderboard, {
      campaignIds: campaignIdsArray,
      limit: 20,
    }),
    enabled: campaignIdsArray.length > 0,
  });

  const { data: topVideosData, isLoading: isTopVideosLoading } = useQuery({
    ...convexQuery(api.analytics.getMultipleCampaignsTopVideos, {
      campaignIds: campaignIdsArray,
      limit: 5,
    }),
    enabled: campaignIdsArray.length > 0,
  });

  const primaryAppId = data?.campaigns?.[0]?.appId as Id<"apps"> | undefined;

  const { data: appInfo } = useQuery({
    ...convexQuery(api.apps.getApp, {
      appId: primaryAppId as Id<"apps">,
    }),
    enabled: Boolean(primaryAppId),
  });

  const dashboardTitle = useMemo(() => {
    if (appInfo?.name) {
      return `${appInfo.name} Dashboard`;
    }

    const campaignNames =
      data?.campaigns?.map((campaign) => campaign.name) ?? [];

    if (campaignNames.length === 1) {
      return `${campaignNames[0]} Dashboard`;
    }

    if (campaignNames.length > 1) {
      return "Campaigns Dashboard";
    }

    return "Dashboard";
  }, [appInfo?.name, data?.campaigns]);

  useEffect(() => {
    if (typeof document !== "undefined" && dashboardTitle) {
      document.title = dashboardTitle;
    }
  }, [dashboardTitle]);

  // Process data for charts - must be called before early returns
  const chartData = useMemo(() => {
    if (!analyticsData?.platforms?.length)
      return { viewsChart: [], videosChart: [], pieChart: [] };

    // Get all unique dates across all platforms
    const allDates = new Set<string>();
    analyticsData.platforms.forEach((platform) => {
      platform.historicalData.forEach((entry: HistoricalDataEntry) => {
        allDates.add(entry.date);
      });
    });

    const sortedDates = Array.from(allDates).sort();

    // Create views chart data
    const viewsChart = sortedDates.map((date) => {
      const dataPoint: Record<string, string | number> = { date };

      analyticsData.platforms.forEach((platform) => {
        const entry = platform.historicalData.find(
          (h: HistoricalDataEntry) => h.date === date,
        );
        dataPoint[platform.platform] = entry ? entry.views : 0;
      });

      return dataPoint;
    });

    // Create videos posted chart data
    const videosChart = sortedDates.map((date) => {
      const dataPoint: Record<string, string | number> = { date };

      analyticsData.platforms.forEach((platform) => {
        const entry = platform.historicalData.find(
          (h: HistoricalDataEntry) => h.date === date,
        );
        dataPoint[platform.platform] = entry ? entry.videosWithData : 0;
      });

      return dataPoint;
    });

    // Create pie chart data - use latest historical data
    const pieChartData: Array<{
      platform: string;
      views: number;
      percentage: number;
      color: string;
    }> = [];

    let totalHistoricalViews = 0;

    // Calculate total views from latest historical data for each platform
    analyticsData.platforms.forEach((platform) => {
      const latestData =
        platform.historicalData[platform.historicalData.length - 1];
      const views = latestData ? latestData.views : 0;
      totalHistoricalViews += views;

      if (views > 0) {
        pieChartData.push({
          platform: platform.platform,
          views: views,
          percentage: 0, // Will calculate after we have total
          color:
            platform.platform === "tiktok"
              ? getTikTokColor()
              : PLATFORM_COLORS[
                  platform.platform as keyof typeof PLATFORM_COLORS
                ] || PLATFORM_COLORS.unknown,
        });
      }
    });

    // Calculate percentages
    const pieChart = pieChartData.map((item) => ({
      ...item,
      percentage:
        totalHistoricalViews > 0
          ? Math.round((item.views / totalHistoricalViews) * 100)
          : 0,
    }));

    return { viewsChart, videosChart, pieChart };
  }, [analyticsData]);

  const hasVideoAnalytics = (analyticsData?.platforms?.length ?? 0) > 0;

  // Early returns after all hooks
  if (isLoading) {
    return (
      <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
        <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col items-center justify-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-primary/60">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!data || campaignIdsArray.length === 0) {
    return (
      <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
        <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col items-center justify-center gap-4">
          <p className="text-lg text-primary/60">No campaigns selected</p>
          <p className="text-sm text-primary/60">
            Please select at least one campaign to view the dashboard.
          </p>
        </div>
      </div>
    );
  }

  // Now we can safely use data since we've checked it above
  const videos = data.videos as Video[];
  const creators = data.creators;
  const campaigns = data.campaigns;
  const contracts = data.contracts as Doc<"creatorContracts">[];
  const videoToContractMap = data.videoToContractMap as Record<string, string>;
  const contractToCreatorMap = data.contractToCreatorMap as Record<
    string,
    string
  >;

  // Calculate stats
  const totalViews = videos.reduce((sum, video) => sum + (video.views ?? 0), 0);
  const totalVideos = videos.length;

  // Calculate unique creators count
  const uniqueCreatorIds = new Set<string>();
  for (const video of videos) {
    const contractId = videoToContractMap[video._id];
    if (contractId) {
      const creatorId = contractToCreatorMap[contractId];
      if (creatorId) {
        uniqueCreatorIds.add(creatorId);
      }
    }
  }
  const uniqueCreatorsCount = uniqueCreatorIds.size;

  // Create a map of contract ID to contract for easy access
  const contractMap = new Map<string, Doc<"creatorContracts">>();
  for (const contract of contracts) {
    contractMap.set(contract._id as unknown as string, contract);
  }

  // Calculate cost based on campaign type
  // If parent campaign (first campaign) is a challenge campaign, use predefinedCPM for all videos
  // If parent campaign is CiR or influencer, use each creator's contract terms (flat fee + CPM)
  const parentCampaign = campaigns.length > 0 ? campaigns[0] : null;
  const isChallengeCampaign = parentCampaign?.type === "challenge";

  // Calculate parent campaign timeline for time period costs
  const campaignStartDate = parentCampaign?._creationTime ?? Date.now();
  const campaignEndDate = parentCampaign?.endDate ?? Date.now();
  const now = Date.now();

  // Use campaign end date if it exists and hasn't passed, otherwise use current date
  const effectiveEndDate = campaignEndDate > now ? now : campaignEndDate;

  // Breakdown tracking
  interface CostBreakdown {
    flatFeePerVideo: number;
    cpmCost: number;
    timePeriodCost: number;
    total: number;
  }

  let cost = 0;
  let predefinedCpm = 0;
  const costBreakdown: CostBreakdown = {
    flatFeePerVideo: 0,
    cpmCost: 0,
    timePeriodCost: 0,
    total: 0,
  };

  if (isChallengeCampaign && parentCampaign?.predefinedCpm) {
    // Challenge campaign: use predefinedCPM for all videos
    predefinedCpm = parentCampaign.predefinedCpm;
    cost = (predefinedCpm * totalViews) / 1000;
    costBreakdown.cpmCost = cost;
    costBreakdown.total = cost;
  } else {
    // CiR or influencer campaign: use each creator's contract terms
    // Group videos by contract to handle "totalViews" type CPM
    const videosByContract = new Map<string, Video[]>();
    for (const video of videos) {
      const contractId = videoToContractMap[video._id];
      if (contractId) {
        const contractIdString = contractId as unknown as string;
        if (!videosByContract.has(contractIdString)) {
          videosByContract.set(contractIdString, []);
        }
        const contractVideosList = videosByContract.get(contractIdString);
        if (contractVideosList) {
          contractVideosList.push(video);
        }
      }
    }

    // Calculate cost for each contract
    for (const [
      contractIdString,
      contractVideos,
    ] of videosByContract.entries()) {
      const contract = contractMap.get(contractIdString);
      if (!contract) continue;

      // Get flat fee per video
      const flatFeePerVideo = contract.flatFee?.perVideo ?? 0;

      // Calculate flat fee costs for all videos in this contract
      const flatFeeCost = flatFeePerVideo * contractVideos.length;
      costBreakdown.flatFeePerVideo += flatFeeCost;

      // Calculate CPM costs based on type
      let cpmCost = 0;

      if (contract.cpmRate?.type === "perVideo") {
        // Per video CPM: calculate cost for each video individually
        for (const video of contractVideos) {
          const views = video.views ?? 0;
          const threshold = contract.cpmRate.threshold ?? 0;
          const cpmRate = contract.cpmRate.rate ?? 0;
          const maxPayout = contract.cpmRate.maxPayout;

          if (views >= threshold) {
            let videoCpmCost = (cpmRate * views) / 1000;

            // Apply max payout cap if exists
            if (maxPayout) {
              videoCpmCost = Math.min(videoCpmCost, maxPayout);
            }

            cpmCost += videoCpmCost;
          }
        }
      } else if (contract.cpmRate?.type === "totalViews") {
        // Total views CPM: calculate based on total views across all videos
        const totalContractViews = contractVideos.reduce(
          (sum, video) => sum + (video.views ?? 0),
          0,
        );
        const threshold = contract.cpmRate.threshold ?? 0;
        const cpmRate = contract.cpmRate.rate ?? 0;
        const maxPayout = contract.cpmRate.maxPayout;

        if (totalContractViews >= threshold) {
          cpmCost = (cpmRate * totalContractViews) / 1000;

          // Apply max payout cap if exists
          if (maxPayout) {
            cpmCost = Math.min(cpmCost, maxPayout);
          }
        }
      }

      costBreakdown.cpmCost += cpmCost;

      // Calculate flat fee time period (retainer) based on parent campaign timeline
      // NOTE: This is calculated once per contract, not per video.
      // If a creator has multiple videos, they're all under the same contract,
      // so the retainer is only calculated once for that contract.
      // Use contract start date if it's later than campaign start, otherwise use campaign start
      let timePeriodCost = 0;
      if (contract.flatFee?.timePeriod) {
        const timePeriodDays = contract.flatFee.timePeriod.days;
        const timePeriodAmount = contract.flatFee.timePeriod.amount;

        // Use contract start date if it's later than campaign start, otherwise use campaign start
        const contractStartDate = contract.startDate;
        const effectiveStartDate =
          contractStartDate > campaignStartDate
            ? contractStartDate
            : campaignStartDate;

        // Calculate days elapsed from effective start to campaign end (or now)
        const contractDaysElapsed = Math.floor(
          (effectiveEndDate - effectiveStartDate) / (1000 * 60 * 60 * 24),
        );

        // Calculate how many time periods have elapsed
        if (contractDaysElapsed >= timePeriodDays && contractDaysElapsed > 0) {
          timePeriodCost =
            Math.floor(contractDaysElapsed / timePeriodDays) * timePeriodAmount;
        }
      }

      costBreakdown.timePeriodCost += timePeriodCost;

      cost += flatFeeCost + cpmCost + timePeriodCost;
    }

    costBreakdown.total = cost;
  }

  // Create a map of creator info for display
  const creatorMap = new Map(creators.map((c) => [c._id as string, c]));

  return (
    <TooltipProvider>
      <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
        <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col gap-8">
          <div className="flex flex-col gap-2">
            <h1 className="text-3xl font-bold text-primary">
              {dashboardTitle}
            </h1>
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            {/* Total Views Card */}
            <div className="rounded-lg border border-border bg-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-primary/60">
                  Total Views
                </h3>
                <Eye className="h-5 w-5 text-primary/60" />
              </div>
              <p className="text-3xl font-semibold text-primary">
                {formatNumber(totalViews)}
              </p>
            </div>

            {/* Total Videos Posted Card */}
            <div className="rounded-lg border border-border bg-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-primary/60">
                  Total Videos Posted
                </h3>
              </div>
              <p className="text-3xl font-semibold text-primary">
                {formatNumber(totalVideos)}
              </p>
            </div>

            {/* Total Unique Creators Card */}
            <div className="rounded-lg border border-border bg-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-primary/60">
                  Total Unique Creators
                </h3>
              </div>
              <p className="text-3xl font-semibold text-primary">
                {formatNumber(uniqueCreatorsCount)}
              </p>
            </div>

            {/* Cost Card */}
            <div className="rounded-lg border border-border bg-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-primary/60">Cost</h3>
              </div>
              <p className="text-3xl font-semibold text-primary">
                {formatCurrency(cost)}
              </p>
              {isChallengeCampaign && predefinedCpm > 0 && (
                <p className="text-xs text-primary/60 mt-2">
                  CPM: {formatCurrency(predefinedCpm)}
                </p>
              )}
              {!isChallengeCampaign && (
                <>
                  <p className="text-xs text-primary/60 mt-2">
                    Based on contract terms
                  </p>
                  {(costBreakdown.flatFeePerVideo > 0 ||
                    costBreakdown.cpmCost > 0 ||
                    costBreakdown.timePeriodCost > 0) && (
                    <Accordion type="single" collapsible className="mt-3">
                      <AccordionItem
                        value="cost-breakdown"
                        className="border-0"
                      >
                        <AccordionTrigger className="text-xs text-primary/60 py-2 hover:no-underline">
                          View cost breakdown
                        </AccordionTrigger>
                        <AccordionContent className="pt-2">
                          <div className="space-y-2 text-xs">
                            {costBreakdown.flatFeePerVideo > 0 && (
                              <div className="flex justify-between items-center">
                                <span className="text-primary/60">
                                  Flat fee per video:
                                </span>
                                <span className="font-medium text-primary">
                                  {formatCurrency(
                                    costBreakdown.flatFeePerVideo,
                                  )}
                                </span>
                              </div>
                            )}
                            {costBreakdown.cpmCost > 0 && (
                              <div className="flex justify-between items-center">
                                <span className="text-primary/60">
                                  CPM costs:
                                </span>
                                <span className="font-medium text-primary">
                                  {formatCurrency(costBreakdown.cpmCost)}
                                </span>
                              </div>
                            )}
                            {costBreakdown.timePeriodCost > 0 && (
                              <div className="flex justify-between items-center">
                                <span className="text-primary/60">
                                  Retainer payments:
                                </span>
                                <span className="font-medium text-primary">
                                  {formatCurrency(costBreakdown.timePeriodCost)}
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between items-center pt-2 border-t border-border">
                              <span className="font-semibold text-primary">
                                Total:
                              </span>
                              <span className="font-semibold text-primary">
                                {formatCurrency(costBreakdown.total)}
                              </span>
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Video Analytics Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Total Views Over Time - Takes 2 columns */}
            <div className="lg:col-span-2">
              <Card className="p-6 min-h-[500px] h-full">
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-primary">
                      Total Views Over Time
                    </h3>
                    <UITooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">
                          Shows views over time for approved videos from
                          selected campaigns.
                        </p>
                      </TooltipContent>
                    </UITooltip>
                  </div>
                  <p className="text-sm text-primary/60 mb-4">
                    Track views across different platforms
                  </p>
                </div>

                {isAnalyticsLoading ? (
                  <div className="h-96 flex items-center justify-center">
                    <div className="text-primary/60">Loading analytics...</div>
                  </div>
                ) : hasVideoAnalytics ? (
                  <div className="h-96">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData.viewsChart}>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          className="opacity-30"
                        />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => {
                            const date = new Date(value);
                            return `${date.getMonth() + 1}/${date.getDate()}`;
                          }}
                        />
                        <YAxis
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => formatNumber(value)}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />

                        {analyticsData?.platforms?.map((platform) => (
                          <Line
                            key={platform.platform}
                            type="monotone"
                            dataKey={platform.platform}
                            stroke={
                              platform.platform === "tiktok"
                                ? getTikTokColor()
                                : PLATFORM_COLORS[
                                    platform.platform as keyof typeof PLATFORM_COLORS
                                  ] || PLATFORM_COLORS.unknown
                            }
                            strokeWidth={3}
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                            name={platform.platform.toUpperCase()}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-96 flex items-center justify-center">
                    <div className="text-center">
                      <Video className="h-12 w-12 text-primary/40 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-primary mb-2">
                        No Video Analytics
                      </h3>
                      <p className="text-sm text-primary/60">
                        No video analytics data available yet
                      </p>
                    </div>
                  </div>
                )}
              </Card>
            </div>

            {/* Views Distribution Pie Chart */}
            <Card className="p-6 min-h-[500px] h-full">
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-primary">
                    Views by Platform
                  </h3>
                  <UITooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        Shows distribution of views across platforms.
                      </p>
                    </TooltipContent>
                  </UITooltip>
                </div>
                <p className="text-sm text-primary/60 mb-4">
                  Distribution across platforms
                </p>
              </div>

              {isAnalyticsLoading ? (
                <div className="h-96 flex items-center justify-center">
                  <div className="text-primary/60">Loading analytics...</div>
                </div>
              ) : hasVideoAnalytics && chartData.pieChart.length > 0 ? (
                <>
                  <div className="h-96">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={chartData.pieChart}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={120}
                          paddingAngle={2}
                          dataKey="views"
                        >
                          {chartData.pieChart.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<PieTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Legend */}
                  <div className="space-y-2 mt-4">
                    {chartData.pieChart.map((entry) => (
                      <div
                        key={entry.platform}
                        className="flex items-center justify-between text-sm"
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: entry.color }}
                          />
                          <PlatformIcon platform={entry.platform} />
                          <span className="capitalize">{entry.platform}</span>
                        </div>
                        <span className="font-medium">{entry.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="h-96 flex items-center justify-center">
                  <div className="text-center">
                    <Video className="h-12 w-12 text-primary/40 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-primary mb-2">
                      No Platform Data
                    </h3>
                    <p className="text-sm text-primary/60">
                      No platform analytics data available yet
                    </p>
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* Videos Posted Over Time */}
          <Card className="p-6 min-h-[500px]">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-semibold text-primary">
                  Videos Posted Over Time
                </h3>
                <UITooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      Shows video publishing activity over time for approved
                      videos.
                    </p>
                  </TooltipContent>
                </UITooltip>
              </div>
              <p className="text-sm text-primary/60 mb-4">
                Track video publishing activity across platforms
              </p>
            </div>

            {isAnalyticsLoading ? (
              <div className="h-96 flex items-center justify-center">
                <div className="text-primary/60">Loading analytics...</div>
              </div>
            ) : hasVideoAnalytics ? (
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData.videosChart}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      className="opacity-30"
                    />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getMonth() + 1}/${date.getDate()}`;
                      }}
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => formatNumber(value)}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />

                    {analyticsData?.platforms?.map((platform) => (
                      <Line
                        key={platform.platform}
                        type="monotone"
                        dataKey={platform.platform}
                        stroke={
                          platform.platform === "tiktok"
                            ? getTikTokColor()
                            : PLATFORM_COLORS[
                                platform.platform as keyof typeof PLATFORM_COLORS
                              ] || PLATFORM_COLORS.unknown
                        }
                        strokeWidth={3}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        name={platform.platform.toUpperCase()}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-96 flex items-center justify-center">
                <div className="text-center">
                  <Video className="h-12 w-12 text-primary/40 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-primary mb-2">
                    No Video Data
                  </h3>
                  <p className="text-sm text-primary/60">
                    No video analytics data available yet
                  </p>
                </div>
              </div>
            )}
          </Card>

          {/* Top Videos */}
          <Card className="p-6 min-h-[400px]">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Video className="h-5 w-5 text-primary/70" />
                <h3 className="text-lg font-semibold text-primary">
                  Top Videos (Approved)
                </h3>
                <UITooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      Shows the best performing approved videos ranked by total
                      views.
                    </p>
                  </TooltipContent>
                </UITooltip>
              </div>
              <p className="text-sm text-primary/60 mb-4">
                Highest performing approved videos
              </p>
            </div>

            {isTopVideosLoading ? (
              <div className="h-48 bg-gray-100 rounded animate-pulse flex items-center justify-center">
                <div className="text-primary/60">Loading top videos...</div>
              </div>
            ) : topVideosData?.videos && topVideosData.videos.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {topVideosData.videos.map((video) => (
                  <div
                    key={video.videoId}
                    className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3"
                  >
                    <div className="space-y-1">
                      <div className="text-lg font-bold text-primary">
                        #{video.rank} = {formatNumber(video.views)} views
                      </div>
                      <div className="text-sm font-semibold text-primary/70">
                        {video.medianMultiplier}x Median
                      </div>
                      <a
                        href={video.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline truncate"
                        title={video.title || "Video"}
                      >
                        {video.title
                          ? video.title.length > 40
                            ? `${video.title.substring(0, 40)}...`
                            : video.title
                          : "Untitled Video"}
                      </a>
                    </div>

                    <div className="space-y-1">
                      <div className="text-sm text-primary/60">
                        by{" "}
                        <span className="font-medium">
                          {video.creator.name}
                        </span>
                      </div>
                      <div className="text-sm text-primary/60">
                        {video.creator.followers > 0
                          ? `${formatNumber(video.creator.followers)} followers`
                          : "Followers: N/A"}
                      </div>
                      <div className="text-sm font-medium text-primary/80 capitalize">
                        {video.platform === "youtube-shorts"
                          ? "YT Short"
                          : video.platform === "youtube"
                            ? "YT Long"
                            : video.platform}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Video className="h-12 w-12 text-primary/40 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-primary mb-2">
                  No Videos Found
                </h3>
                <p className="text-sm text-primary/60">
                  No approved videos found in the selected campaigns.
                </p>
              </div>
            )}
          </Card>

          {/* Leaderboard */}
          <Card className="p-6 min-h-[500px]">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-semibold text-primary">
                  Top Creators
                </h3>
                <UITooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      Shows the top creators based on their total views and
                      engagement metrics.
                    </p>
                  </TooltipContent>
                </UITooltip>
              </div>
              <p className="text-sm text-primary/60 mb-4">
                Overview of the most active creators in the selected campaigns.
              </p>
            </div>

            {isLeaderboardLoading ? (
              <div className="h-96 bg-gray-100 rounded animate-pulse flex items-center justify-center">
                <div className="text-primary/60">Loading leaderboard...</div>
              </div>
            ) : leaderboardData && leaderboardData.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-primary">
                        Rank
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-primary">
                        Creator
                      </th>
                      <th className="text-right py-3 px-4 font-semibold text-primary">
                        Total Views
                      </th>
                      <th className="text-right py-3 px-4 font-semibold text-primary">
                        Total Likes
                      </th>
                      <th className="text-right py-3 px-4 font-semibold text-primary">
                        Videos
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboardData.map((creator, index) => (
                      <tr
                        key={creator.creatorId}
                        className="border-b border-gray-100 dark:border-gray-700"
                      >
                        <td className="py-3 px-4 text-left text-primary">
                          <span className="text-primary/60">
                            {index === 0
                              ? "🥇"
                              : index === 1
                                ? "🥈"
                                : index === 2
                                  ? "🥉"
                                  : `#${index + 1}`}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-left text-primary">
                          <div className="flex items-center gap-3">
                            <img
                              src={creator.avatarUrl}
                              alt={creator.name}
                              className="h-8 w-8 rounded-full object-cover"
                            />
                            <span className="font-medium">{creator.name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right text-primary">
                          {formatNumber(creator.totalViews)}
                        </td>
                        <td className="py-3 px-4 text-right text-primary">
                          {formatNumber(creator.totalLikes)}
                        </td>
                        <td className="py-3 px-4 text-right text-primary">
                          {formatNumber(creator.totalVideos)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-primary/40 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-primary mb-2">
                  No Creators Found
                </h3>
                <p className="text-sm text-primary/60">
                  No creators have posted approved videos in the selected
                  campaigns.
                </p>
              </div>
            )}
          </Card>

          {/* Videos Table */}
          <div className="rounded-lg border border-border bg-card">
            <div className="overflow-x-auto">
              {videos.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <p className="mb-4 text-lg text-primary/60">
                    No approved videos found
                  </p>
                  <p className="text-sm text-primary/60">
                    Approved videos from the selected campaigns will appear
                    here.
                  </p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="border-b border-border bg-secondary">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                        Title
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                        Creator
                      </th>
                      <th className="w-16 px-6 py-3 text-center text-sm font-medium text-primary/60">
                        Platform
                      </th>
                      <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                        Views
                      </th>
                      <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                        Likes
                      </th>
                      <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                        Bookmarks
                      </th>
                      <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                        Shares
                      </th>
                      <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                        Comments
                      </th>
                      <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                        Date Posted
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {videos.map((video) => {
                      // Find creator for this video through contract mapping
                      const contractId = videoToContractMap[video._id];
                      const creatorId = contractId
                        ? contractToCreatorMap[contractId]
                        : null;
                      const creator = creatorId
                        ? creatorMap.get(creatorId as Id<"creators">)
                        : null;

                      return (
                        <tr key={video._id} className="hover:bg-secondary/50">
                          <td className="px-6 py-4">
                            <a
                              href={video.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-primary hover:underline"
                            >
                              {truncateText(video.title ?? "No title", 60)}
                            </a>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-primary">
                              {creator?.name ?? "Unknown Creator"}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex justify-center">
                              <PlatformIcon platform={video.platform ?? ""} />
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right text-sm text-primary">
                            {formatNumber(video.views ?? 0)}
                          </td>
                          <td className="px-6 py-4 text-right text-sm text-primary">
                            {formatNumber(video.likes ?? 0)}
                          </td>
                          <td className="px-6 py-4 text-right text-sm text-primary">
                            {video.platform === "youtube" ||
                            video.platform === "youtube-shorts" ||
                            video.platform === "tiktok"
                              ? "N/A"
                              : formatNumber(video.bookmarks ?? 0)}
                          </td>
                          <td className="px-6 py-4 text-right text-sm text-primary">
                            {video.platform === "youtube" ||
                            video.platform === "youtube-shorts"
                              ? "N/A"
                              : formatNumber(video.shares ?? 0)}
                          </td>
                          <td className="px-6 py-4 text-right text-sm text-primary">
                            {formatNumber(video.comments ?? 0)}
                          </td>
                          <td className="px-6 py-4 text-right text-sm text-primary">
                            {video.postDate
                              ? format(new Date(video.postDate), "dd/MM/yy")
                              : "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
